import React from "react";
import {
  Cog,
  Factory,
  Car,
  HeartPulse,
  Smile,
  GraduationCap,
  Gem,
  Clapperboard,
  ArrowRight,
} from "lucide-react";
import { industries } from "../data/mock";

const iconMap = {
  Cog,
  Factory,
  Car,
  HeartPulse,
  Smile,
  GraduationCap,
  Gem,
  Clapperboard,
};

export const IndustriesSection = () => {
  return (
    <section className="py-24 lg:py-32 bg-[#F8F8F8] relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-[#F76B1C]/[0.03] rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16 lg:mb-20">
          <div className="max-w-2xl">
            <span className="text-[#F76B1C] text-sm font-semibold uppercase tracking-[0.15em]">
              Industries
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111111] leading-tight">
              Transforming Industries with SLA Printing
            </h2>
            <p className="mt-6 text-lg text-[#666666] leading-relaxed">
              From engineering prototypes to surgical models, Formlabs SLA
              printers are trusted across industries where precision and
              reliability are non-negotiable.
            </p>
          </div>
          <a
            href="#case-studies"
            onClick={(e) => {
              e.preventDefault();
              const el = document.querySelector("#case-studies");
              if (el)
                window.scrollTo({
                  top: el.getBoundingClientRect().top + window.scrollY - 80,
                  behavior: "smooth",
                });
            }}
            className="inline-flex items-center gap-2 text-[#F76B1C] hover:text-[#E05A0F] font-semibold transition-colors duration-200 group shrink-0"
          >
            View Case Studies
            <ArrowRight
              size={18}
              className="group-hover:translate-x-1 transition-transform duration-200"
            />
          </a>
        </div>

        {/* Industries grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
          {industries.map((industry, i) => {
            const IconComponent = iconMap[industry.icon];
            return (
              <div
                key={i}
                className="group relative rounded-2xl overflow-hidden cursor-pointer aspect-[4/5]"
              >
                {/* Background image */}
                <img loading="lazy"
                  src={industry.image}
                  alt={industry.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                />

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/5 group-hover:from-black/90 group-hover:via-black/40 transition-all duration-500" />

                {/* Orange accent line at top */}
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#F76B1C]/0 group-hover:bg-[#F76B1C]/80 transition-all duration-300" />

                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-5 lg:p-6">
                  <div className="w-10 h-10 rounded-lg bg-[#F76B1C]/[0.15] backdrop-blur-md border border-[#F76B1C]/20 flex items-center justify-center mb-3 group-hover:bg-[#F76B1C]/25 transition-colors duration-300">
                    {IconComponent && (
                      <IconComponent size={18} className="text-[#F76B1C]" />
                    )}
                  </div>
                  <h3 className="text-base lg:text-lg font-bold text-white mb-0.5">
                    {industry.name}
                  </h3>
                  <p className="text-xs lg:text-sm text-white/50 font-medium">
                    {industry.subtitle}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
